import React, { useEffect } from 'react';
import { CountryProvider } from '@/contexts/CountryContext';
import Navbar from '@/components/zera/Navbar';
import Footer from '@/components/zera/Footer';
import ZeraPartners from '@/components/zera/ZeraPartners';
import RegionExplorer from '@/components/zera/romania/RegionExplorer';
import LuxuryExperiences from '@/components/zera/romania/LuxuryExperiences';
import FeaturedHotels from '@/components/zera/romania/FeaturedHotels';
import RomaniaMapSection from '@/components/zera/romania/RomaniaMapSection';
import CuratedItineraries from '@/components/zera/romania/CuratedItineraries';
import DestinationGallery from '@/components/zera/romania/DestinationGallery';
import TravelInspiration from '@/components/zera/romania/TravelInspiration';
import RomaniaInquiry from '@/components/zera/romania/RomaniaInquiry';
import { HUB_IMG } from '@/data/romaniaHub';
import { ChevronDown } from 'lucide-react';

const scrollTo = (id: string) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth' });
};

const Hero: React.FC = () => (
  <section className="relative h-screen min-h-[640px] w-full overflow-hidden">
    <img
      src={HUB_IMG.heroA}
      alt="Bran Castle and misty Carpathian mountains at golden hour, Romania."
      className="absolute inset-0 w-full h-full object-cover scale-105"
      loading="eager"
      fetchPriority="high"
    />
    <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/35 to-black/70" />

    <div className="relative z-10 h-full flex flex-col justify-center items-center text-center px-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-7 animate-fade-in">
        <div className="w-8 h-[1px] bg-[#C9A961]" />
        <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961]">
          Zera Travel · Romania Destination Hub
        </span>
        <div className="w-8 h-[1px] bg-[#C9A961]" />
      </div>
      <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light text-white leading-[1.1] mb-6">
        Discover Romania Beyond the Guidebooks
      </h1>
      <p className="font-sans text-sm md:text-base font-light text-white/75 max-w-2xl leading-relaxed mb-10">
        Experience medieval cities, luxury castles, mountain retreats, hidden villages and
        unforgettable adventures — curated exclusively by Zera Travel.
      </p>
      <div className="flex flex-col sm:flex-row gap-4">
        <button
          onClick={() => scrollTo('plan-journey')}
          className="font-sans text-[11px] tracking-[0.25em] uppercase px-10 py-4 bg-[#C9A961] text-white hover:bg-[#b8944d] transition-colors duration-300"
        >
          Plan My Journey
        </button>
        <button
          onClick={() => scrollTo('regions')}
          className="font-sans text-[11px] tracking-[0.25em] uppercase px-10 py-4 border border-white/50 text-white hover:bg-white hover:text-[#1A3B5C] transition-colors duration-300"
        >
          Explore Destinations
        </button>
      </div>
    </div>

    <button
      onClick={() => scrollTo('regions')}
      aria-label="Scroll down"
      className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/70 hover:text-white animate-bounce"
    >
      <ChevronDown className="w-7 h-7" />
    </button>
  </section>
);

const RomaniaDestinationsInner: React.FC = () => {
  const goInquire = () => scrollTo('plan-journey');


  // Lightweight SEO + structured data without an external helmet dependency.
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Romania Destination Hub | Luxury Travel Curated by Zera Travel';

    const setMeta = (attr: 'name' | 'property', key: string, content: string) => {
      let tag = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
      return tag;
    };

    const desc = setMeta('name', 'description',
      'Discover Romania beyond the guidebooks — medieval cities, luxury castles, the Carpathians, the Danube Delta, Transylvania and hidden villages, curated by Zera Travel.');
    const ogTitle = setMeta('property', 'og:title', 'Romania Destination Hub | Zera Travel');
    const ogImage = setMeta('property', 'og:image', HUB_IMG.heroA);

    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'TouristDestination',
      name: 'Romania',
      description:
        'Luxury curated travel across Romania — castles, the Carpathians, the Danube Delta, Transylvania, Maramureș and Bucovina.',
      touristType: 'Luxury travelers',
      provider: { '@type': 'TravelAgency', name: 'Zera Travel' },
    });
    document.head.appendChild(ld);

    return () => {
      document.title = prevTitle;
      ld.remove();
      [desc, ogTitle, ogImage].forEach((t) => t && t.remove());
    };
  }, []);

  return (
    <div className="theme-romania min-h-screen bg-[#FAF8F3] scroll-smooth">

      <Navbar />
      <Hero />
      <CuratedItineraries onInquire={goInquire} />
      <RegionExplorer />
      <LuxuryExperiences onInquire={goInquire} />
      <FeaturedHotels />
      <RomaniaMapSection />
      <DestinationGallery />
      <TravelInspiration onInquire={goInquire} />
      <RomaniaInquiry />
      <ZeraPartners />
      <Footer />

    </div>
  );
};

const RomaniaDestinations: React.FC = () => (
  <CountryProvider country="romania">
    <RomaniaDestinationsInner />
  </CountryProvider>
);

export default RomaniaDestinations;
